/**
 * atendimento.service.js
 *
 * Orquestração compartilhada entre `view-atendimento.vue` e
 * `atendimento-popup.vue`. Centraliza lógica idêntica das duas superfícies
 * (load de conexões, retries de ação destrutiva, fire-and-forget de
 * syncChats, wiring de GlobalBus) e expõe factories parametrizáveis para
 * os comportamentos divergentes (timer de sync pós-pareamento, poller de
 * conversas, retries escalonados do QR connect) — cada componente passa o
 * seu tuning e mantém o próprio ciclo de vida.
 *
 * Princípios:
 *  - Sem singletons: cada factory cria instância isolada.
 *  - Sem `this`: funções recebem `store`/`apiAtendimento`/`conexoes` como
 *    parâmetros explícitos.
 *  - Sem side-effect oculto em GlobalBus: o wiring é retornado pronto
 *    para o componente registrar/desregistrar no seu ciclo de vida.
 *  - Factories retornam `{ start, stop, running }` — componente guarda a
 *    instância em `data()` e chama `stop()` em `beforeDestroy`.
 */

import { ACTION } from '@constants/Text'
import { pluralize } from '@utils/Format'
import { apiAtendimento } from '@modules/atendimento/api/apiAtendimento'
import { shouldMarkSelectedAsRead } from '@modules/atendimento/utils/conversa-helpers'
import { msgKey } from '@modules/atendimento/store/mutations'
import {
  parseBackendDate,
  truncatePreview,
} from '@modules/atendimento/utils/message-formatter'
import { MENSAGENS_PER_PAGE } from '@modules/atendimento/constants/Text'

export const DESTRUCTIVE_CONEXAO_ACTIONS = Object.freeze([
  'disconnected',
  'deactivated',
  'deleted',
])

// Ações destrutivas que invalidam o sync em andamento — o consumer deve
// parar o timer de sync ao receber uma delas.
export function isDestructiveConexaoAction(action) {
  return DESTRUCTIVE_CONEXAO_ACTIONS.includes(action)
}

// Conexões vivem em `data()` do consumer (não em Vuex). Retorna [] em falha
// pra manter o contrato dos consumers (nunca null).
export async function loadConexoes(apiAtendimento) {
  const r = await apiAtendimento.listarConexoes()
  if (r && r.status === 200) return r.value || []
  return []
}

// Pure helper: retorna null se a conexão da conversa selecionada sumiu da
// lista (sinal pro consumer limpar seleção); caso contrário, o `selected`.
export function pickSelectedIfConexaoRemovida(selected, conexoes) {
  if (!selected || !selected.id_conexao) return selected
  const list = Array.isArray(conexoes) ? conexoes : []
  const stillExists = list.some((c) => c && c.id === selected.id_conexao)
  return stillExists ? selected : null
}

// Fire-and-forget syncChats por conexão conectada. Backend retorna 200 na
// hora e roda em background; falhas absorvidas — sync é best-effort.
export function fireSyncChatsForConectadas(apiAtendimento, conexoes) {
  const conns = (Array.isArray(conexoes) ? conexoes : []).filter(
    (c) => c && c.status === 'conectado'
  )
  for (const conn of conns) {
    apiAtendimento.syncChats({ id_conexao: conn.id }).catch(() => {
      // best-effort — falha de sync não bloqueia UI
    })
  }
  return conns.length
}

// Aplica regra de escopo do refresh-chats-list:
//  - gestor + filtro → só a conexão filtrada
//  - gestor sem filtro → todas conectadas
//  - !gestor + idProfissional → só conexões vinculadas (c.profissionais)
//  - !gestor + idProfissional ausente → [] (DEFESA: nunca exponha todas
//    as conexões a um não-gestor sem identidade resolvida; backend
//    revalida via validate_conexao_access, mas o frontend não deve
//    sequer disparar requests indevidos).
function filterRefreshTargets(
  conexoes,
  { idProfissional, isGestor, activeConexaoFilter }
) {
  const conectadas = (Array.isArray(conexoes) ? conexoes : []).filter(
    (c) => c && c.status === 'conectado'
  )
  if (isGestor && activeConexaoFilter) {
    return conectadas.filter((c) => c.id === activeConexaoFilter)
  }
  if (!isGestor) {
    // Sem idProfissional não há como provar vínculo — fail-closed.
    if (!idProfissional) return []
    return conectadas.filter(
      (c) =>
        Array.isArray(c.profissionais) &&
        c.profissionais.some((p) => p.id_profissional === idProfissional)
    )
  }
  // Gestor sem filtro: dispara em todas conectadas.
  return conectadas
}

// Agrega resultados do Promise.allSettled em { sucessos, falhas, novos_*}.
// status===200 conta como sucesso (inclui already_running:true).
function aggregateRefreshResults(results) {
  const summary = { sucessos: 0, falhas: 0, novos_grupos: 0, novos_contatos: 0 }
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value && r.value.status === 200) {
      summary.sucessos += 1
      const v = r.value.value || {}
      summary.novos_grupos += v.novos_grupos || 0
      summary.novos_contatos += v.novos_contatos || 0
    } else {
      summary.falhas += 1
    }
  }
  return summary
}

/**
 * Dispara refresh-chats-list em paralelo nas conexões VISÍVEIS ao usuário.
 *
 * Regra de escopo:
 *  - isGestor && activeConexaoFilter → apenas a conexão filtrada
 *  - isGestor && !activeConexaoFilter → todas conectadas do escritório
 *  - !isGestor → apenas conexões em que o profissional está vinculado
 *    (via c.profissionais.some(p => p.id_profissional === idProfissional))
 *
 * Sempre filtra status === 'conectado'. Promise.allSettled (best-effort).
 * already_running:true no body conta como SUCESSO (status===200) — não
 * agrega novos_grupos/novos_contatos pra essa conexão.
 *
 * Returns: { total, sucessos, falhas, novos_grupos, novos_contatos }
 */
export async function fireRefreshChatsListForVisible(
  apiAtendimento,
  conexoes,
  scope = {}
) {
  const alvos = filterRefreshTargets(conexoes, scope)
  if (alvos.length === 0) {
    return {
      total: 0,
      sucessos: 0,
      falhas: 0,
      novos_grupos: 0,
      novos_contatos: 0,
    }
  }
  const results = await Promise.allSettled(
    alvos.map((c) => apiAtendimento.refreshChatsList({ id_conexao: c.id }))
  )
  const summary = aggregateRefreshResults(results)
  return { total: alvos.length, ...summary }
}

// Helpers de pluralização PT-BR — nomes context-specific (snack do
// refresh) mas o switch singular/plural delega para o helper canônico
// `pluralize(val, singular, plural)` do @utils/Format (convenção do
// projeto — usado por acoes-lote, mix-resumo, summary-valuation, etc.).
function pluralizeConexoes(n) {
  return n + ' ' + pluralize(n, 'conexão atualizada', 'conexões atualizadas')
}

function pluralizeNovosGrupos(n) {
  if (!n) return ''
  return ' (' + n + ' ' + pluralize(n, 'novo grupo', 'novos grupos') + ')'
}

function pluralizeFalhas(n) {
  return n + ' ' + pluralize(n, 'falha', 'falhas')
}

/**
 * Constrói o payload de snack a partir do resultado agregado de
 * fireRefreshChatsListForVisible. Centraliza a composição de copy para
 * evitar duplicação entre view-atendimento e atendimento-popup, e
 * garante pluralização PT-BR correta nos 4 cenários:
 *
 *   - total=0          → warning "Nenhuma conexão conectada para atualizar"
 *   - falhas=0         → success "Conversas atualizadas (N novos grupos)"
 *   - sucessos=0       → error "Falha ao atualizar conversas..."
 *   - parcial          → warning "N conexões atualizadas. M falhas..."
 *
 * Returns: { text, color, button } pronto pra `this.$showSnack(...)`.
 */
export function buildRefreshFeedback(result) {
  if (!result || result.total === 0) {
    return {
      text: 'Nenhuma conexão conectada para atualizar',
      color: 'warning',
      button: true,
    }
  }
  if (result.falhas === 0) {
    return {
      text: 'Conversas atualizadas' + pluralizeNovosGrupos(result.novos_grupos),
      color: 'success',
      button: true,
    }
  }
  if (result.sucessos === 0) {
    return {
      text: 'Falha ao atualizar conversas. Tente novamente em alguns minutos.',
      color: 'error',
      button: true,
    }
  }
  return {
    text:
      pluralizeConexoes(result.sucessos) +
      '. ' +
      pluralizeFalhas(result.falhas) +
      ' — tente novamente.',
    color: 'warning',
    button: true,
  }
}

// Retries silent de listarConversas após delete de conexão — backend apaga
// histórico async, então sem retries conversas fantasmas persistem até o
// próximo poll. Retorna clear() pra cancelar timeouts em unmount.
export function scheduleDeletedConversasRetries(
  store,
  { delays = [1000, 3000], silent = true } = {}
) {
  const handles = []
  for (const delay of delays) {
    handles.push(
      setTimeout(() => {
        store.dispatch('atendimento/listarConversas', { _silent: silent })
      }, delay)
    )
  }
  return function clear() {
    for (const h of handles) clearTimeout(h)
    handles.length = 0
  }
}

// Timer de reload pós-pareamento — popula sidebar enquanto backend sincroniza
// histórico. View: 6×10s (1min); popup: 12×10s (2min). start() é idempotente.
// onEnd dispara em maxTries (útil pra resetar syncingChats).
export function createSyncReloadTimer(
  store,
  { maxTries, intervalMs = 10000, silent = true, extraParams = {}, onEnd = null } = {}
) {
  let timer = null
  let tries = 0

  function stop() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function start() {
    stop()
    tries = 0
    timer = setInterval(() => {
      tries++
      store.dispatch('atendimento/listarConversas', {
        _silent: silent,
        ...extraParams,
      })
      if (tries >= maxTries) {
        stop()
        if (typeof onEnd === 'function') onEnd()
      }
    }, intervalMs)
  }

  return {
    start,
    stop,
    get running() {
      return timer !== null
    },
  }
}

/**
 * Poller principal de conversas (5s). Durante `syncingChats` (pós-pair)
 * usa `listarConversas` silent como fallback, porque `pollConversas`
 * (merge por `since`) não captura conversas que o backend ainda está
 * populando.
 *
 * Parâmetros:
 *  - `getIsSyncing`: callback para saber se está no modo "sync pós-pair".
 *  - `onPoll`: recebido o resultado de `pollConversas`. View usa pra
 *    fazer delta de mensagens do chat aberto + `checkDeliveryStatuses`;
 *    popup não precisa (passa `undefined`).
 *  - `onError`: recebido o erro do `pollConversas`. View conta erros
 *    consecutivos e para após `maxConsecutiveErrors`; popup ignora.
 *  - `maxConsecutiveErrors`: após N erros seguidos, para o polling até
 *    novo `start()`. Default `Infinity` (nunca para).
 *  - `checkVisibility`: se true, pula ticks enquanto `document.hidden`.
 *    View usa pra economizar bateria/rede; popup não.
 *  - `silentDuringSync`: default true — passa `_silent:true` no dispatch
 *    durante `getIsSyncing()` pra não piscar skeleton.
 *
 * Retorna `{ start, stop, running }`.
 */
function findUpdatedSelected(r, selected) {
  if (!selected || !r || !r.value) return null
  const updated = r.value.conversas_atualizadas || []
  return updated.find((c) => c.id === selected.id) || null
}

async function fetchSelectedMensagens(idConversa) {
  const r = await apiAtendimento.listarMensagens({
    id_conversa: idConversa,
    per_page: MENSAGENS_PER_PAGE,
  })
  if (!r || r.status !== 200) return []
  return (r.value && r.value.mensagens) || []
}

// Campos cujo UPDATE via webhook (reaction, status de entrega, edição,
// REVOKE) precisa refletir na UI mesmo em mensagem já no store — antes o
// poll só fazia ADD e descartava esses updates (só apareciam após F5).
const MENSAGEM_UPDATE_FIELDS = [
  'reacoes',
  'status',
  'editada',
  'conteudo',
  'conteudo_original',
  'media_url',
  // `data_mensagem`: sincroniza precisão de segundos quando o store recebeu
  // o timestamp de outra rota — sem isso, mensagens do mesmo minuto empatam
  // no sort e o desempate por id pode embaralhar a ordem.
  'data_mensagem',
]

function shallowDiffersOnFields(a, b, fields) {
  for (const f of fields) {
    const av = a[f]
    const bv = b[f]
    // Comparação por valor, com fallback pra JSON quando o campo é objeto/array
    // (ex.: `reacoes`), pra não disparar reactivity à toa.
    if (av === bv) continue
    const aIsObj = av !== null && typeof av === 'object'
    const bIsObj = bv !== null && typeof bv === 'object'
    if (aIsObj || bIsObj) {
      if (JSON.stringify(av) !== JSON.stringify(bv)) return true
    } else {
      return true
    }
  }
  return false
}

// data_mensagem mais recente da lista local (ms). Inclui optimisticas — são
// as mais novas e indicam atividade local.
function latestLocalMensagemMs(mensagens) {
  if (!Array.isArray(mensagens) || mensagens.length === 0) return null
  let max = null
  for (const m of mensagens) {
    if (!m) continue
    const d = parseBackendDate(m.data_mensagem)
    const ms = d ? d.getTime() : null
    if (ms != null && (max === null || ms > max)) max = ms
  }
  return max
}

/**
 * Heurística barata de "conversa defasada" para o sync-on-open (ADR-2):
 * mostra o DB local primeiro e só dispara o sync incremental (background) se
 * ALGUM sinal indicar defasagem:
 *  1. Poucas mensagens locais (< threshold) → DB ainda não populado.
 *  2. `conversa.ultima_mensagem_em` mais recente que a última local → chegou
 *     algo que não está no store.
 *  3. Há `ultima_mensagem_preview` mas nenhuma mensagem local.
 *
 * Pura/sem side-effect; null/undefined → não força sync.
 *
 * @param {Object} conversa
 * @param {Array}  mensagens — mensagens locais no store
 * @param {number} threshold — piso de mensagens locais
 * @returns {boolean}
 */
export function isConversaStale(conversa, mensagens, threshold) {
  if (!conversa) return false
  const locais = Array.isArray(mensagens) ? mensagens : []
  if (locais.length < threshold) return true
  const ultimaServerMs = (() => {
    const d = parseBackendDate(conversa.ultima_mensagem_em)
    return d ? d.getTime() : null
  })()
  const ultimaLocalMs = latestLocalMensagemMs(locais)
  if (ultimaServerMs != null && ultimaLocalMs != null) {
    if (ultimaServerMs > ultimaLocalMs) return true
  }
  const temPreview = !!(
    conversa.ultima_mensagem_preview &&
    String(conversa.ultima_mensagem_preview).trim()
  )
  if (temPreview && locais.length === 0) return true
  return false
}

// Indexa por chave canônica (whatsapp_message_id || id, ADR-3) E pelo `id`
// quando difere. Indexar por AMBOS resolve a assimetria: cópia local só com
// `id` (sync antigo sem wa_id) precisa casar quando o poll traz a mesma
// mensagem já com `whatsapp_message_id` — senão entraria como duplicata.
function buildMensagensById(mensagens) {
  const map = {}
  if (!Array.isArray(mensagens)) return map
  for (const msg of mensagens) {
    if (!msg) continue
    const key = msgKey(msg)
    if (key != null) map[key] = msg
    if (msg.id != null && msg.id !== key) map[msg.id] = msg
  }
  return map
}

// Recupera a cópia local equivalente à `msg` recebida do poll, testando
// tanto a chave canônica quanto o `id` puro (cobre os dois lados da
// assimetria wa_id presente/ausente).
function findExisting(existingMap, msg) {
  const key = msgKey(msg)
  if (key != null && existingMap[key]) return existingMap[key]
  if (msg.id != null && existingMap[msg.id]) return existingMap[msg.id]
  return null
}

// Match contra optimistic local não substituída — defensivo contra race onde
// o poll traz a real antes do POST de enviarMensagem retornar. Casa por
// `whatsapp_message_id`; o fuzzy `tipo+conteudo` só entra quando NENHUM lado
// tem wa_id (optimisticas ainda não recebem wa_id do backend).
function findOptimisticMatch(currentMsgs, real) {
  if (!real || real.direcao !== 'saida') return null
  if (real.whatsapp_message_id) {
    const byWa = currentMsgs.find(
      (m) =>
        m && m._optimistic && m.whatsapp_message_id === real.whatsapp_message_id
    )
    if (byWa) return byWa
    // Real com wa_id sem optimistic correspondente → não cai no fuzzy; será
    // reconciliada pelo REPLACE_OPTIMISTIC quando o POST retornar.
    return null
  }
  // Sem wa_id dos dois lados: fallback por tipo + conteudo (optimistics são
  // fila local do próprio user e raramente repetem a string em sequência).
  return (
    currentMsgs.find(
      (m) =>
        m &&
        m._optimistic &&
        !m.whatsapp_message_id &&
        m.tipo === real.tipo &&
        (m.conteudo || '') === (real.conteudo || '')
    ) || null
  )
}

// Reconcilia/insere UMA mensagem nova do poll no store local.
function commitOneMensagem(store, currentMsgs, existingMap, msg) {
  const existing = findExisting(existingMap, msg)
  // "Existente" optimistic → REPLACE, não UPDATE-merge: senão a optimistic
  // sobrevive mesclada e a real não toma o lugar dela.
  if (existing && existing._optimistic) {
    store.commit('atendimento/REPLACE_OPTIMISTIC_MENSAGEM', {
      temp_id: existing.id,
      real: msg,
    })
    return
  }
  if (!existing) {
    const optimistic = findOptimisticMatch(currentMsgs, msg)
    if (optimistic) {
      // Poll chegou antes do REPLACE_OPTIMISTIC — substitui agora pra não
      // duplicar; o REPLACE posterior vira no-op (idempotente).
      store.commit('atendimento/REPLACE_OPTIMISTIC_MENSAGEM', {
        temp_id: optimistic.id,
        real: msg,
      })
    } else {
      store.commit('atendimento/ADD_MENSAGEM', msg)
    }
    return
  }
  if (shallowDiffersOnFields(existing, msg, MENSAGEM_UPDATE_FIELDS)) {
    // UPDATE_MENSAGEM faz merge raso — mandar só id + campos relevantes evita
    // sobrescrever dado local (ex.: `temp_id`, anexos) com null do servidor.
    const patch = { id: msg.id }
    for (const f of MENSAGEM_UPDATE_FIELDS) patch[f] = msg[f]
    store.commit('atendimento/UPDATE_MENSAGEM', patch)
  }
}

// Teto do preview otimista de `ultima_mensagem_preview` (mesmo teto do backend
// via listarConversas), pra não inflar o objeto da conversa no store.
const CONVERSA_PREVIEW_MAX_LENGTH = 100

// Mensagem cronologicamente mais nova da lista (maior `data_mensagem`). Ignora
// optimisticas — sem timestamp confiável do servidor, não definem o preview.
function pickNewestMensagem(mensagens) {
  if (!Array.isArray(mensagens) || mensagens.length === 0) return null
  let newest = null
  let newestMs = null
  for (const m of mensagens) {
    if (!m || m._optimistic) continue
    const d = parseBackendDate(m.data_mensagem)
    const ms = d ? d.getTime() : null
    if (ms != null && (newestMs === null || ms > newestMs)) {
      newest = m
      newestMs = ms
    }
  }
  return newest
}

/**
 * Patch de preview/horário do item da conversa na lista lateral, derivado das
 * mensagens do chat aberto. MONOTÔNICO: só retorna patch quando a mais nova é
 * estritamente mais recente que o `ultima_mensagem_em` atual — nunca regride
 * (mensagem antiga reprocessada pelo poll não sobrescreve a última real).
 *
 * Pura/sem side-effect — o caller decide se commita.
 *
 * @param {Object|null} conversa  - item atual da lista lateral
 * @param {Array<Object>} mensagens - mensagens do chat aberto
 * @returns {{ id: *, ultima_mensagem_em: string, ultima_mensagem_preview: string }|null}
 */
export function buildConversaPreviewPatch(conversa, mensagens) {
  if (!conversa || conversa.id == null) return null
  const newest = pickNewestMensagem(mensagens)
  if (!newest) return null

  const newestDate = parseBackendDate(newest.data_mensagem)
  if (!newestDate) return null

  const atualDate = parseBackendDate(conversa.ultima_mensagem_em)
  // Guarda monotônica: havendo timestamp atual, só avança se a nova é mais
  // recente. Sem timestamp atual (conversa nova), aplica.
  if (atualDate && newestDate.getTime() <= atualDate.getTime()) return null

  return {
    id: conversa.id,
    ultima_mensagem_em: newest.data_mensagem,
    ultima_mensagem_preview: truncatePreview(
      newest.conteudo || '',
      CONVERSA_PREVIEW_MAX_LENGTH
    ),
  }
}

// Reconcilia as mensagens novas do poll com `state.mensagens` e — quando o
// caller passa a `conversa` correspondente da lista lateral — atualiza
// monotonicamente seu preview/horário, sem esperar o próximo `listarConversas`.
export function commitNewMensagens(store, currentMsgs, newMsgs, conversa) {
  const existingMap = buildMensagensById(currentMsgs)
  for (const msg of newMsgs) {
    if (msgKey(msg) == null) continue
    commitOneMensagem(store, currentMsgs, existingMap, msg)
  }
  if (!conversa) return
  // Preview computado do estado já mesclado (locais + novas), não só das
  // `newMsgs`, pra que o "mais recente" seja consistente se o delta vier
  // fora de ordem.
  const merged = [].concat(
    Array.isArray(currentMsgs) ? currentMsgs : [],
    Array.isArray(newMsgs) ? newMsgs : []
  )
  const patch = buildConversaPreviewPatch(conversa, merged)
  if (patch) store.commit('atendimento/UPDATE_CONVERSA', patch)
}

// onPoll default — sem um onPoll próprio, o poller ainda precisa puxar delta
// de mensagens da conversa aberta e refrescar delivery status; senão o chat
// fica congelado até reabrir a conversa (caso do popup, que não passa onPoll).
async function defaultOnPoll(store, r) {
  // Defesa: store sem `getters` (testes minimalistas) — fallback null.
  const getters = store.getters || {}
  const selected = getters['atendimento/selectedConversa']
  if (findUpdatedSelected(r, selected)) {
    const newMsgs = await fetchSelectedMensagens(selected.id)
    const currentMsgs = getters['atendimento/mensagens'] || []
    // Conversa correspondente na lista lateral, pra que o preview/horário
    // acompanhe a última mensagem real sem aguardar o próximo listarConversas.
    const conversas = getters['atendimento/conversas'] || []
    const conversaLateral = conversas.find((c) => c && c.id === selected.id)
    commitNewMensagens(store, currentMsgs, newMsgs, conversaLateral)
  }
  if (selected) {
    store.dispatch('atendimento/checkDeliveryStatuses')
  }
}

// Auto-mark-as-read do chat aberto. Roda SEMPRE após cada pollConversas
// bem-sucedido, independente do consumer ter passado um `onPoll` próprio
// (view-atendimento passa o seu pra controlar skeleton/visibility — sem
// esse hook fora do onPoll, o badge não zerava na view).
function autoMarkSelectedAsRead(store, r) {
  if (!r || !r.value) return
  // Defesa: store sem `getters` (testes minimalistas) ou sem o getter
  // específico não pode derrubar o tick do poller.
  const selected = store.getters
    ? store.getters['atendimento/selectedConversa']
    : null
  const updated = r.value.conversas_atualizadas || []
  if (!shouldMarkSelectedAsRead(selected, updated)) return
  // Optimistic update zera o badge na lista lateral imediatamente; a
  // action chama a API em fire-and-forget e reconcilia no próximo poll.
  store.dispatch('atendimento/marcarComoLida', {
    id_conversa: selected.id,
  })
}

export function createConversasPoller(
  store,
  {
    intervalMs = 5000,
    getIsSyncing = () => false,
    onPoll = null,
    onError = null,
    maxConsecutiveErrors = Infinity,
    checkVisibility = false,
    silentDuringSync = true,
    syncExtraParams = {},
  } = {}
) {
  // Aplica default — consumer pode passar onPoll explícito pra estender
  // (view passa o seu pra manter o controle do skeleton/visibility).
  if (onPoll === null) onPoll = (r) => defaultOnPoll(store, r)
  let timer = null
  let stopped = true
  let lastPollTimestamp = null
  let consecutiveErrors = 0
  // Guard de in-flight: evita empilhar pollConversas quando o backend está
  // lento — cada request preso seguraria um worker (amplificador do storm
  // outbound, incidente 5122). O setInterval fixo dispara um novo tick a cada
  // 5s independente do anterior ter respondido; sem este guard, eles acumulam.
  let inFlight = false

  function stop() {
    stopped = true
    inFlight = false
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function shouldSkipTick() {
    if (stopped) return true
    if (checkVisibility && typeof document !== 'undefined' && document.hidden)
      return true
    return false
  }

  async function pollSuccess(r) {
    if (stopped) return
    lastPollTimestamp = new Date().toISOString()
    consecutiveErrors = 0
    if (typeof onPoll === 'function') await onPoll(r)
  }

  function pollFailure(err) {
    if (stopped) return
    consecutiveErrors++
    if (typeof onError === 'function') onError(err)
    if (consecutiveErrors >= maxConsecutiveErrors) stop()
  }

  async function tick() {
    if (shouldSkipTick()) return
    // Pula o tick se o pollConversas anterior ainda está aguardando resposta.
    if (inFlight) return
    inFlight = true
    if (getIsSyncing()) {
      // Durante sync pós-pair, listarConversas é necessário pra pegar
      // conversas que o backend ainda está populando (pollConversas com
      // since=ts não vê inserts cujo data_atualizacao já passou). Mas
      // ainda assim disparamos pollConversas em paralelo — sem ele, o
      // chat aberto fica congelado durante toda a janela de sync (até
      // 2min) porque o handler onPoll é o único caminho que faz delta
      // de mensagens da conversa selecionada.
      store.dispatch('atendimento/listarConversas', {
        _silent: silentDuringSync,
        ...syncExtraParams,
      })
    }
    try {
      const r = await store.dispatch('atendimento/pollConversas', {
        since: lastPollTimestamp,
      })
      // Auto-mark-as-read roda antes do onPoll do consumer pra que o
      // comportamento independa de qual onPoll cada componente passa
      // (view tem o seu próprio handlePollResult, popup usa default).
      autoMarkSelectedAsRead(store, r)
      await pollSuccess(r)
    } catch (err) {
      pollFailure(err)
    } finally {
      inFlight = false
    }
  }

  function start() {
    stop()
    stopped = false
    lastPollTimestamp = new Date().toISOString()
    consecutiveErrors = 0
    timer = setInterval(tick, intervalMs)
  }

  return {
    start,
    stop,
    get running() {
      return !stopped && timer !== null
    },
  }
}

/**
 * Retries escalonados de uma função de reload — usado no handler de
 * `ATENDIMENTO_CONEXAO_CONECTADA` para contornar a race do webhook da
 * Evolution API (modal QR detecta "conectado" direto da Evolution e
 * emite antes do backend refletir no DB).
 *
 * `delays` (ms) do primeiro até o último disparo; o valor `0` dispara
 * imediatamente. Default `[0, 1000, 3000]` bate com o popup hoje; a view
 * passa `[0, 1500, 4000]`.
 *
 * Retorna um `cancel()` que limpa os timers pendentes.
 */
export function createConnectRetries(
  reloadFn,
  { delays = [0, 1000, 3000] } = {}
) {
  const handles = []
  for (const delay of delays) {
    if (delay === 0) {
      Promise.resolve().then(() => {
        try {
          reloadFn()
        } catch {
          // best-effort — erros num reload não derrubam os demais
        }
      })
      continue
    }
    handles.push(
      setTimeout(() => {
        try {
          reloadFn()
        } catch {
          // idem
        }
      }, delay)
    )
  }
  return function cancel() {
    for (const h of handles) clearTimeout(h)
    handles.length = 0
  }
}

// Mapa nome-de-handler → evento GlobalBus. View registra todos; popup
// registra um subset (sem transferida / contato-vinculado).
export const ATENDIMENTO_LISTENERS = Object.freeze({
  onConversaCriada: ACTION.ATENDIMENTO_CONVERSA_CRIADA,
  onHistoricoImportado: ACTION.ATENDIMENTO_HISTORICO_IMPORTADO,
  onConexaoConectada: ACTION.ATENDIMENTO_CONEXAO_CONECTADA,
  onConexaoAtualizada: ACTION.ATENDIMENTO_CONEXAO_ATUALIZADA,
  onConversaTransferida: ACTION.ATENDIMENTO_CONVERSA_TRANSFERIDA,
  onContatoVinculado: ACTION.ATENDIMENTO_CONTATO_VINCULADO,
})

// Modos válidos retornados pelo backend no POST /contatos/<id>/auto-link.
// Mantemos local para evitar import cross-camada (constants ↔ services
// teriam ciclo) e porque o contrato é estritamente do atendimento.
export const AUTO_LINK_MODES = Object.freeze({
  AUTO: 'auto',
  SUGGEST: 'suggest',
  NONE: 'none',
  ALREADY_LINKED: 'already_linked',
})

const VALID_AUTO_LINK_MODES = new Set(Object.values(AUTO_LINK_MODES))

/**
 * Normaliza a resposta do POST /contatos/<id>/auto-link no shape
 * `{mode, idCliente, candidates}` consumível pelos Vue components.
 *
 * Regras (cinto e suspensório — defesa contra qualquer drift do backend):
 *  - response null/undefined → {mode:'none', idCliente:null, candidates:[]}
 *  - status != 200 → {mode:'none', idCliente:null, candidates:[]} (UI
 *    cai no estado neutro, snack global mostra a mensagem de erro)
 *  - value ausente OU value.mode ausente → 'none'
 *  - mode desconhecido (ex.: backend novo lançou shape não previsto) → 'none'
 *  - mode='auto'|'already_linked' → idCliente vem de value.id_cliente_app
 *  - mode='suggest' → candidates vem de value.candidates (filtrado para
 *    garantir que é array; se não for, retorna [])
 *  - mode='none' → idCliente=null, candidates=[]
 *
 * @param {Object|null} r — resposta crua de apiAtendimento.autoLinkContato
 * @returns {{mode: string, idCliente: number|null, candidates: Array}}
 */
export function parseAutoLinkResponse(r) {
  const fallback = { mode: AUTO_LINK_MODES.NONE, idCliente: null, candidates: [] }
  if (!r || r.status !== 200 || !r.value) return fallback
  const value = r.value
  const mode = value.mode
  if (!VALID_AUTO_LINK_MODES.has(mode)) return fallback
  if (mode === AUTO_LINK_MODES.AUTO || mode === AUTO_LINK_MODES.ALREADY_LINKED) {
    return {
      mode: mode,
      idCliente: value.id_cliente_app || null,
      candidates: [],
    }
  }
  if (mode === AUTO_LINK_MODES.SUGGEST) {
    return {
      mode: mode,
      idCliente: null,
      candidates: Array.isArray(value.candidates) ? value.candidates : [],
    }
  }
  // mode === 'none'
  return { mode: AUTO_LINK_MODES.NONE, idCliente: null, candidates: [] }
}

// Registra apenas os handlers presentes em `handlers` (chaves seguem
// ATENDIMENTO_LISTENERS). Retorna unregister() pra usar no beforeDestroy.
export function registerAtendimentoListeners(globalBus, handlers) {
  const pairs = []
  const safe = handlers || {}
  for (const key of Object.keys(ATENDIMENTO_LISTENERS)) {
    const fn = safe[key]
    if (typeof fn !== 'function') continue
    const event = ATENDIMENTO_LISTENERS[key]
    globalBus.$on(event, fn)
    pairs.push([event, fn])
  }
  return function unregister() {
    for (const [event, fn] of pairs) globalBus.$off(event, fn)
    pairs.length = 0
  }
}
